import React, { useState } from 'react';
import { InfoMessage } from 'features/components/InfoMessage';
import { Switch } from 'features/components/Switch';
import { useGetProjectOrganizationCollaboratorsQuery } from 'features/entry-form-v2/data/useGetProjectOrganizationCollaboratorsQuery';
import { useSelector } from 'hooks/utils';
import { LanguageType } from 'types';
import { Input } from 'features/entry-form-v2/component/Input';

// Organizations are basically groups, backend and frontend names are not aligned :(
export type OrganizationPermission = {
	organizationId: number;
	readAccess: boolean;
	writeAccess: boolean;
	deleteAccess: boolean;
	analysisAccess: boolean;
};

interface GroupsStepProps {
	selectedGroups: { groupId: number; groupName: string }[];
	setSelectedGroups: (groups: { groupId: number; groupName: string }[]) => void;
	projectId: string;
}

export const GroupsStep: React.FC<GroupsStepProps> = ({
	selectedGroups,
	setSelectedGroups,
	projectId
}) => {
	const language = useSelector(state => state.ui.i18n.language);
	const translation = DICTIONARY[language];

	const [searchTerm, setSearchTerm] = useState('');

	const { data: project, isLoading } = useGetProjectOrganizationCollaboratorsQuery({
		projectId: projectId
	});

	const filteredOrganizations = project?.project.organizations.filter(org =>
		org.organizationName.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleToggleGroup = (groupId: number) => {
		if (selectedGroups.some(group => group.groupId === groupId)) {
			setSelectedGroups(selectedGroups.filter(group => group.groupId !== groupId));
		} else {
			setSelectedGroups([
				...selectedGroups,
				{
					groupId: groupId,
					groupName:
						filteredOrganizations?.find(org => org.organizationId === groupId)
							?.organizationName || ''
				}
			]);
		}
	};

	return (
		<div>
			<h2 className="text-2xl font-bold text-gray-800 mb-6">{translation.selectGroups}</h2>

			{/* Search input */}
			<div className="mb-6">
				<Input
					label={translation.searchGroups}
					id="search-groups"
					type="text"
					placeholder={translation.searchGroups}
					value={searchTerm}
					onChange={e => setSearchTerm(e.target.value)}
				/>
			</div>

			{/* Organizations list */}
			<div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8">
				<div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200">
					<h3 className="text-lg font-semibold text-gray-800">
						{translation.availableGroups}
					</h3>
				</div>

				{isLoading ? (
					<div className="p-6 text-center text-gray-500">{translation.loadingGroups}</div>
				) : filteredOrganizations?.length === 0 ? (
					<div className="p-6 text-center text-gray-500">
						{searchTerm ? translation.noMatchingGroups : translation.noGroupsAvailable}
					</div>
				) : (
					<div className="divide-y divide-gray-100">
						{filteredOrganizations?.map(org => (
							<div
								key={org.organizationId}
								className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
								onClick={() => handleToggleGroup(org.organizationId)}
							>
								<div>
									<h4 className="text-sm font-medium text-gray-700">
										{org.organizationName}
									</h4>
									<p className="text-xs text-gray-500 mt-1">
										{org.organizationCollaborators?.length || 0}{' '}
										{translation.collaborator}
									</p>
								</div>
								<Switch
									on={selectedGroups.some(
										group => group.groupId === org.organizationId
									)}
									onChange={() => handleToggleGroup(org.organizationId)}
								/>
							</div>
						))}
					</div>
				)}
			</div>

			<InfoMessage message={translation.infoMessage} />
		</div>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		selectGroups: 'Select Groups',
		searchGroups: 'Search groups...',
		availableGroups: 'Available Groups',
		loadingGroups: 'Loading groups...',
		noMatchingGroups: 'No matching groups found',
		noGroupsAvailable: 'No groups available',
		default: 'Default',
		collaborator: 'collaborator',
		infoMessage:
			'Users will be added to the selected groups. Group permissions will apply to these users once they accept the invitation.'
	},
	[LanguageType.Norwegian]: {
		selectGroups: 'Velg grupper',
		searchGroups: 'Søk grupper...',
		availableGroups: 'Tilgjengelige grupper',
		loadingGroups: 'Laster grupper...',
		noMatchingGroups: 'Ingen matchende grupper funnet',
		noGroupsAvailable: 'Ingen grupper tilgjengelig',
		default: 'Standard',
		collaborator: 'medarbeider',
		infoMessage:
			'Brukere vil bli lagt til i de valgte gruppene. Gruppe tillatelser vil gjelde for disse brukerne når de aksepterer invitasjonen.'
	}
};
